import axios from 'axios';
import { ApiResponse } from '../types';
import { API_ORIGIN, API_URL } from '../config';

// API URL 설정
const API_URL_LOCAL = API_ORIGIN;

// 웨어러블 타입 정의
export interface Wearable {
  id: number;
  name: string;
  code: string;
  type: string;
  status: string;
  pool_id: number;
  pool_code: string;
  created_at: string | null;
  updated_at: string | null;
  is_active: boolean;
  device_id?: string;
  device_type?: string;
  battery_level?: number;
  firmware_version?: string;
  last_sync_at?: string | null;
  current_member_id?: number;
  usage_purpose?: string;
}

// 웨어러블 매핑 타입 정의
export interface WearableMapping {
  id: number;
  member_id: number;
  wearable_id: number;
  assigned_at: string;
  returned_at: string | null;
}

export interface WearableDevice {
  id: number;
  device_id: string;
  status: string;
  last_seen: string;
  battery_level: number;
  pool_id: number;
}

// API 응답 처리 함수
const handleApiResponse = <T>(response: any): ApiResponse<T> => {
  console.log('[wearableApi] 응답 처리:', response);
  return {
    success: response.status >= 200 && response.status < 300,
    data: response.data,
    message: response.status >= 200 && response.status < 300 
      ? '요청 성공' 
      : `요청 실패 (상태 ${response.status})`
  };
};

// API 오류 처리 함수
const handleApiError = <T>(error: any): ApiResponse<T> => {
  console.error('[wearableApi] 오류 처리:', error);
  let message = '알 수 없는 오류';
  
  if (error.response?.data?.detail) {
    message = error.response.data.detail;
  } else if (error.response?.data?.message) {
    message = error.response.data.message;
  } else if (error.message) {
    message = error.message;
  }

  return {
    success: false,
    data: null,
    message
  };
};

// wearableApi 객체 정의
export const wearableApi = {
  // 사용 가능한 웨어러블 목록 조회
  getAvailableWearables: async (poolId: string | number): Promise<ApiResponse<Wearable[]>> => {
    try {
      console.log(`[wearableApi] 시설 ID ${poolId}의 사용 가능한 웨어러블 목록 조회 시작`);
      
      const response = await axios.get(`${API_URL_LOCAL}/api/pools/${poolId}/wearables`);
      console.log(`[wearableApi] 모든 웨어러블 목록 조회 응답:`, response.data);
      
      // 상태가 ACTIVE(대소문자 무관)이고 is_active가 true인 웨어러블만 필터링
      const availableWearables = Array.isArray(response.data) 
        ? response.data.filter((wearable: Wearable) => {
            const status = String(wearable.status || '').toUpperCase();
            return (status === 'ACTIVE') && wearable.is_active === true && !wearable.current_member_id;
          })
        : [];
      
      console.log(`[wearableApi] 필터링된 사용 가능한 웨어러블 수: ${availableWearables.length}`);
      
      return {
        success: response.status >= 200 && response.status < 300,
        data: availableWearables,
        message: availableWearables.length > 0 
          ? '사용 가능한 웨어러블 목록 조회 성공' 
          : '사용 가능한 웨어러블 없음'
      };
    } catch (error) {
      return handleApiError<Wearable[]>(error);
    }
  },

  // 모든 웨어러블 목록 조회
  getAllWearables: async (poolId: string | number): Promise<ApiResponse<Wearable[]>> => {
    try {
      console.log(`[wearableApi] 시설 ID ${poolId}의 모든 웨어러블 목록 조회 시작`);
      const response = await axios.get(`${API_URL_LOCAL}/api/pools/${poolId}/wearables`);
      console.log(`[wearableApi] 모든 웨어러블 목록 조회 응답:`, response.data);
      return handleApiResponse<Wearable[]>(response);
    } catch (error) {
      return handleApiError<Wearable[]>(error);
    }
  },

  // 회원의 웨어러블 반납
  returnWearable: async (poolId: number, memberId: number): Promise<ApiResponse<any>> => {
    try {
      console.log('[wearableApi] 회원 ID', memberId, '의 웨어러블 반납 시작');
      const response = await axios.put(`${API_URL_LOCAL}/api/pools/${poolId}/members/${memberId}/wearable/return`);
      return {
        success: true,
        message: '웨어러블이 반납됨',
        data: response.data
      };
    } catch (error) {
      console.error('[wearableApi] 오류 처리:', error);
      return {
        success: false,
        message: '웨어러블 반납 중 오류 발생',
        data: null
      };
    }
  },

  // 회원의 웨어러블 사용 이력 조회
  getMemberWearableHistory: async (poolId: string | number, memberId: number): Promise<ApiResponse<WearableMapping[]>> => {
    try {
      console.log(`[wearableApi] 회원 ID ${memberId}의 웨어러블 사용 이력 조회 시작`);
      const response = await axios.get(`${API_URL_LOCAL}/api/pools/${poolId}/members/${memberId}/wearables`);
      console.log(`[wearableApi] 웨어러블 사용 이력 조회 응답:`, response.data);
      return handleApiResponse<WearableMapping[]>(response);
    } catch (error) {
      return handleApiError<WearableMapping[]>(error);
    }
  },

  // 회원의 현재 활성화된 웨어러블 매핑 찾기
  getActiveMemberWearable: async (poolId: string | number, memberId: number): Promise<ApiResponse<WearableMapping | null>> => {
    try {
      console.log(`[wearableApi] 회원 ID ${memberId}의 현재 활성화된 웨어러블 매핑 조회 시작`);
      const response = await axios.get(`${API_URL_LOCAL}/api/pools/${poolId}/members/${memberId}/wearables?active=true`);
      console.log(`[wearableApi] 활성화된 웨어러블 매핑 조회 응답:`, response.data);
      
      // 응답 데이터가 배열인 경우 첫 번째 항목만 반환
      const data = response.data && Array.isArray(response.data) && response.data.length > 0 
        ? response.data[0] 
        : null;

      return {
        success: response.status >= 200 && response.status < 300,
        data,
        message: response.status >= 200 && response.status < 300 
          ? '요청 성공' 
          : `요청 실패 (상태 ${response.status})`
      };
    } catch (error) {
      return handleApiError<WearableMapping | null>(error);
    }
  },

  // 웨어러블 매핑 기록 초기화 (모든 할당/반납 기록 초기화)
  resetAllWearableMappings: async (poolId: number): Promise<ApiResponse<any>> => {
    try {
      console.log(`[wearableApi] 시설 ID ${poolId}의 모든 웨어러블 매핑 기록 초기화 시작`);
      const response = await axios.post(
        `${API_URL_LOCAL}/api/pools/${poolId}/wearables/reset-mappings`
      );
      console.log(`[wearableApi] 웨어러블 매핑 기록 초기화 응답:`, response.data);
      
      // 응답 데이터가 있는 경우 해당 데이터를 사용
      if (response.data) {
        return {
          success: true,
          message: response.data.message || '모든 웨어러블 매핑이 초기화됨',
          data: response.data
        };
      }
      
      // 응답 데이터가 없는 경우 기본 성공 메시지 반환
      return {
        success: true,
        message: '모든 웨어러블 매핑이 초기화됨',
        data: null
      };
    } catch (error) {
      console.error('[wearableApi] 오류 처리:', error);
      return {
        success: false,
        message: '웨어러블 매핑 초기화 중 오류 발생',
        data: null
      };
    }
  },

  // 회원에게 웨어러블 할당
  assignWearable: async (poolId: number, memberId: number, wearableId: number): Promise<WearableMapping> => {
    const response = await axios.post(
      `${API_URL}/api/pools/${poolId}/members/${memberId}/wearables`,
      { wearable_id: wearableId }
    );
    return response.data;
  },

  // 회원의 웨어러블 반납
  returnWearableFromMember: async (poolCode: string, memberId: number, mappingId: number): Promise<ApiResponse<any>> => {
    try {
      const response = await axios.put(
        `${API_URL_LOCAL}/api/pools/${poolCode}/members/${memberId}/wearables/${mappingId}/return`
      );
      return {
        success: true,
        data: response.data ?? null,
        message: response.data?.message || '웨어러블 반납 처리 성공'
      };
    } catch (error) {
      return handleApiError<any>(error);
    }
  },
  
  // 회원의 웨어러블 할당 이력 조회
  getMemberWearables: async (poolId: number, memberId: number): Promise<WearableMapping[]> => {
    const response = await axios.get(`${API_URL}/api/pools/${poolId}/members/${memberId}/wearables`);
    return response.data;
  },
  
  // 회원의 현재 활성화된 웨어러블 조회
  getActiveMemberWearables: async (poolId: number, memberId: number): Promise<WearableMapping[]> => {
    const response = await axios.get(`${API_URL}/api/pools/${poolId}/members/${memberId}/wearables?active=true`);
    return response.data;
  },
  
  // ... existing code ...
  getPoolDevices: async (poolId: number): Promise<WearableDevice[]> => {
    const response = await axios.get(`${API_URL}/api/pools/${poolId}/devices`);
    return response.data;
  },

  getPoolAvailableDevices: async (poolId: number): Promise<WearableDevice[]> => {
    const response = await axios.get(`${API_URL}/api/pools/${poolId}/available-devices`);
    return response.data;
  },

  // ... existing code ...
}; 